import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { paths } from '@common/types/api.types';
import { postRequest } from '@config/axios/axios.config';
import {
  initialValues,
  loginPasswordSchema,
} from '../schemas/loginPassword.schema';
import { LoginContext } from '../context';
import { formatApiError } from '@common/errors/error-formatting';
import { useCaptchaDataQuery } from '@common/cache';

interface ILoginPasswordFormShape {
  email: string;
  password: string;
  captcha_answer: string;
}

export const useLoginPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const {
    data = { id: '', svg: '' },
    refetch,
    isFetching,
  } = useCaptchaDataQuery();

  const { handleNextStep } = useContext(LoginContext);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [httpError, setHttpError] = useState<string>('');

  const invalidToken = location.state?.invalidToken;

  useEffect(() => {
    if (invalidToken) {
      setHttpError(
        'This magic login link has already been used or is invalid. Please request a new link.',
      );
    }
  }, [invalidToken]);

  const formMethods = useForm<ILoginPasswordFormShape>({
    mode: 'onChange',
    resolver: yupResolver(loginPasswordSchema),
    defaultValues: initialValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting },
    setValue,
  } = formMethods;

  const submitHandler = async (values: ILoginPasswordFormShape) => {
    try {
      setHttpError('');

      const response = await postRequest<
        paths['/auth/login/password']['post']['requestBody']['content']['application/json'],
        paths['/auth/login/password']['post']['responses']['200']['content']['application/json']
      >('/auth/login/password', {
        email: values.email,
        password: values.password,
        captcha_answer: values.captcha_answer,
        captcha_id: data.id,
      });

      navigate(`/auth/login/mfa?token=${response.data.token}`, {
        state: {
          mfaType: response.data.mfa_type,
          maskedPhoneNumber: response.data?.mfa_phone_number,
        },
      });
    } catch (error) {
      refetch();
      setValue('captcha_answer', '');
      setHttpError(formatApiError(error));
    }
  };

  const onSubmit = handleSubmit(submitHandler);

  const handleForgotPassword = () => {
    navigate('/auth/request-password-reset');
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleRefresh = () => {
    refetch();
  };

  return {
    handleForgotPassword,
    onSubmit,
    isSubmitting,
    handleNextStep,
    showPassword,
    toggleShowPassword,
    httpError,
    svg: data.svg,
    handleRefresh,
    loading: isFetching,
    formMethods,
  };
};
